import React, { useState, useEffect, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { Navigation } from '@/components/Navigation'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ChevronLeft, ChevronRight, Eye, RefreshCw } from 'lucide-react'
import { getScanTypeDisplayName } from '@/utils/displayNames'
import { fetchWithRefresh } from '@/utils/fetchWithRefresh'

interface Scan {
  id: string
  url: string
  scan_type: string
  status: 'queued' | 'running' | 'completed' | 'failed'
  created_at: string
}

interface ScanListResponse {
  scans: Scan[]
  total: number
  page: number
  per_page: number
}

const ScanHistory: React.FC = () => {
  const [scans, setScans] = useState<Scan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalScans, setTotalScans] = useState(0)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [perPage] = useState(25)

  const fetchScans = useCallback(async (page = 1, status = 'all') => {
    try {
      setLoading(true)
      setError(null)

      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      })

      if (status !== 'all') {
        params.append('status', status)
      }

      const response = await fetchWithRefresh(`/api/scan/list?${params}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (response.ok) {
        const data: ScanListResponse = await response.json()
        setScans(data.scans || [])
        setTotalScans(data.total || 0)
        setTotalPages(Math.ceil(data.total / perPage))
        setCurrentPage(data.page || 1)
      } else if (response.status === 401) {
        setError('Please log in to view your scan history')
      } else {
        setError('Failed to load scan history')
      }
    } catch (err) {
      setError('Network error - please try again')
    } finally {
      setLoading(false)
    }
  }, [perPage])

  useEffect(() => {
    fetchScans(currentPage, statusFilter)
  }, [fetchScans, currentPage, statusFilter])

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      completed: 'default',
      running: 'secondary', 
      queued: 'outline',
      failed: 'destructive'
    }
    
    return (
      <Badge variant={variants[status] || 'outline'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    )
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'

    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return 'Invalid Date'

      // Format as: "Sep 14, 2025 at 10:30 PM"
      const dateStr = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
      return `${dateStr} at ${timeStr}`
    } catch (error) {
      return 'Invalid Date'
    }
  }

  const formatScanType = (scanType: string) => {
    return getScanTypeDisplayName(scanType)
  }

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status)
    setCurrentPage(1) // Reset to first page when filtering
  }

  const handleRefresh = () => {
    fetchScans(currentPage, statusFilter)
  }

  if (loading && scans.length === 0) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
              <p>Loading scan history...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={handleRefresh} variant="outline">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen w-full flex-col">
      <Helmet>
        <title>Scan History | Fusegu Security</title>
        <meta name="description" content="View your complete scan history and past security assessments." />
      </Helmet>
      <Navigation />
      <div className="container mx-auto py-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Scan History</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {totalScans} total scans
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={handleStatusFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="running">Running</SelectItem>
                  <SelectItem value="queued">Queued</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                onClick={handleRefresh} 
                variant="outline" 
                size="sm"
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {scans.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No scans found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {statusFilter !== 'all' && 'Try changing the status filter or '}
                Start your first security scan to see results here.
              </p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>URL</TableHead>
                      <TableHead>Scan Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scans.map((scan) => (
                      <TableRow key={scan.id}>
                        <TableCell>
                          <div className="max-w-xs truncate" title={scan.url}>
                            {scan.url}
                          </div>
                        </TableCell>
                        <TableCell>
                          {formatScanType(scan.scan_type)}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(scan.status)}
                        </TableCell>
                        <TableCell>
                          {formatDate(scan.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            disabled={scan.status !== 'completed'}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Results
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalScans)} of {totalScans} scans
                  </p>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  )
}

export default ScanHistory